import { WTBProtocol, WTBProtocolData } from "views/wtb/WTBProtocol";

export enum WTBGamePhase {
  WAITING = "waiting",
  ANSWER = "answer",
  VOTING = "voting",
  RESULTS = "results",
  AI_VOTING = "aiVoting",
  FINAL = "final",
}

export interface WTBPlayer {
  id: number;
  name: string;
  avatar: string;
  isActive: boolean;
  isBot: boolean;
  score: number;
  answers: (string | null)[];
  votes: (number | null)[];
  finalVote?: number;
}

export interface WTBAnswer {
  playerId: number;
  answer: string;
  isBot: boolean;
  votes?: number;
}

export interface WTBState {
  players: WTBPlayer[];
  currentRound: number;
  totalRounds: number;
  roundTime: number;
  leftTime: number;
  phase: WTBGamePhase;
  currentQuestion: string;
  selectedAnswerId?: number;
  isVoteConfirmed: boolean;
  roundResults: {
    round: number;
    topAnswers: WTBAnswer[];
    botAnswers: WTBAnswer[];
  }[];
  finalResults?: {
    botPlayers: WTBPlayer[];
    correctGuesserIds: number[];
    totalPlayers: number;
  };
  currentRoundAnswerIndices: number[];
  questions: string[];
  botCount: number;
  selectedAIVote?: number;
  pendingAnswer?: string;
}

export const initialWTBState: WTBState = {
  players: [],
  currentRound: 0,
  totalRounds: 5,
  roundTime: 0,
  leftTime: 0,
  phase: WTBGamePhase.ANSWER,
  currentQuestion: "",
  isVoteConfirmed: false,
  roundResults: [],
  currentRoundAnswerIndices: [],
  questions: [],
  botCount: 0,
  selectedAIVote: undefined,
  pendingAnswer: undefined,
};

export type WTBAction =
  | { type: "START_GAME"; players: WTBPlayer[]; botCount: number }
  | { type: "SET_QUESTION"; question: string }
  | { type: "SUBMIT_ANSWER"; playerId: number; answer: string }
  | { type: "SELECT_ANSWER"; answerId: number }
  | { type: "CONFIRM_VOTE" }
  | { type: "DECREMENT_TIME" }
  | { type: "NEXT_PHASE" }
  | { type: "SELECT_FINAL_VOTE"; playerId: number }
  | { type: "SELECT_AI_VOTE"; playerId: number }
  | { type: "SET_PENDING_ANSWER"; answer: string }
  | {
      type: "SOCKET_ROUND_START";
      payload: WTBProtocolData[typeof WTBProtocol.ROUND_START];
    }
  | {
      type: "SOCKET_VOTING_START";
      payload: WTBProtocolData[typeof WTBProtocol.VOTING_START];
    }
  | {
      type: "SOCKET_ANSWER_SUBMITTED";
      payload: WTBProtocolData[typeof WTBProtocol.ANSWER_SUBMITTED];
    }
  | {
      type: "SOCKET_VOTE_RESULT";
      payload: WTBProtocolData[typeof WTBProtocol.VOTE_RESULT];
    }
  | {
      type: "SOCKET_AI_VOTING_START";
      payload: WTBProtocolData[typeof WTBProtocol.AI_VOTING_START];
    }
  | {
      type: "SOCKET_FINAL_RESULTS";
      payload: WTBProtocolData[typeof WTBProtocol.FINAL_RESULTS];
    };

export function wtbReducer(state: WTBState, action: WTBAction): WTBState {
  switch (action.type) {
    case "START_GAME":
      return {
        ...initialWTBState,
        players: action.players,
        botCount: action.botCount,
        phase: WTBGamePhase.ANSWER,
      };

    case "SET_QUESTION":
      return {
        ...state,
        currentQuestion: action.question,
      };

    case "SUBMIT_ANSWER":
      return {
        ...state,
        players: state.players.map((player) =>
          player.id === action.playerId
            ? {
                ...player,
                answers: [
                  ...player.answers.slice(0, state.currentRound - 1),
                  action.answer,
                  ...player.answers.slice(state.currentRound),
                ],
              }
            : player
        ),
      };

    case "SELECT_ANSWER":
      return {
        ...state,
        selectedAnswerId: action.answerId,
      };

    case "CONFIRM_VOTE":
      if (!state.selectedAnswerId) return state;
      return {
        ...state,
        isVoteConfirmed: true,
        players: state.players.map((player) =>
          player.id === state.selectedAnswerId
            ? {
                ...player,
                votes: [
                  ...player.votes.slice(0, state.currentRound - 1),
                  (player.votes[state.currentRound - 1] || 0) + 1,
                  ...player.votes.slice(state.currentRound),
                ],
              }
            : player
        ),
      };

    case "DECREMENT_TIME":
      return {
        ...state,
        leftTime: Math.max(0, state.leftTime - 0.1),
      };

    case "NEXT_PHASE":
      const nextPhase = {
        [WTBGamePhase.WAITING]: WTBGamePhase.WAITING,
        [WTBGamePhase.ANSWER]: WTBGamePhase.VOTING,
        [WTBGamePhase.VOTING]: WTBGamePhase.RESULTS,
        [WTBGamePhase.RESULTS]: WTBGamePhase.ANSWER,
        [WTBGamePhase.AI_VOTING]: WTBGamePhase.FINAL,
        [WTBGamePhase.FINAL]: WTBGamePhase.FINAL,
      }[state.phase];

      if (state.phase === WTBGamePhase.ANSWER && state.pendingAnswer) {
        // 소켓 서비스는 컴포넌트에서 처리
      }

      return {
        ...state,
        phase: nextPhase,
        isVoteConfirmed: false,
        selectedAnswerId: undefined,
        selectedAIVote: undefined,
        pendingAnswer: undefined,
        players: state.players.map((player) => ({
          ...player,
          finalVote: undefined,
        })),
      };

    case "SELECT_FINAL_VOTE":
      return {
        ...state,
        players: state.players.map((player) =>
          player.id === action.playerId
            ? { ...player, finalVote: action.playerId }
            : player
        ),
      };

    case "SELECT_AI_VOTE":
      return {
        ...state,
        selectedAIVote: action.playerId,
      };

    case "SET_PENDING_ANSWER":
      return {
        ...state,
        pendingAnswer: action.answer,
      };

    case "SOCKET_ROUND_START":
      return {
        ...state,
        currentQuestion: action.payload.question,
        currentRound: action.payload.round,
        totalRounds: action.payload.totalRounds,
        roundTime: action.payload.leftTime,
        leftTime: action.payload.leftTime,
        phase: WTBGamePhase.ANSWER,
        players: action.payload.players,
        isVoteConfirmed: false,
        selectedAnswerId: undefined,
        pendingAnswer: undefined,
      };

    case "SOCKET_VOTING_START":
      const updatedPlayers = state.players.map((player) => {
        const playerAnswer = action.payload.answers.find(
          (a) => a.playerId === player.id
        );
        if (playerAnswer) {
          return {
            ...player,
            answers: [
              ...player.answers.slice(0, state.currentRound - 1),
              playerAnswer.answer,
              ...player.answers.slice(state.currentRound),
            ],
          };
        }
        return player;
      });

      return {
        ...state,
        phase: WTBGamePhase.VOTING,
        leftTime: action.payload.leftTime,
        players: updatedPlayers,
        currentRoundAnswerIndices: action.payload.answers.map(
          (answer) => answer.playerId
        ),
        isVoteConfirmed: false,
        selectedAnswerId: undefined,
      };

    case "SOCKET_ANSWER_SUBMITTED":
      return {
        ...state,
        players: state.players.map((player) =>
          player.id === action.payload.playerId
            ? {
                ...player,
                answers: [
                  ...player.answers.slice(0, state.currentRound - 1),
                  action.payload.answer,
                  ...player.answers.slice(state.currentRound),
                ],
              }
            : player
        ),
      };

    case "SOCKET_VOTE_RESULT":
      return {
        ...state,
        phase: WTBGamePhase.RESULTS,
        players: action.payload.players,
        roundResults: [
          ...state.roundResults,
          {
            round: action.payload.round,
            topAnswers: action.payload.topAnswers,
            botAnswers: action.payload.botAnswers,
          },
        ],
      };

    case "SOCKET_AI_VOTING_START":
      return {
        ...state,
        phase: WTBGamePhase.AI_VOTING,
        leftTime: action.payload.leftTime,
        selectedAIVote: undefined,
        players: action.payload.players.map((player) => ({
          ...player,
          finalVote: undefined,
        })),
      };

    case "SOCKET_FINAL_RESULTS":
      return {
        ...state,
        phase: WTBGamePhase.FINAL,
        players: action.payload.players,
        finalResults: {
          botPlayers: action.payload.botPlayers,
          correctGuesserIds: action.payload.correctGuesserIds,
          totalPlayers: action.payload.totalPlayers,
        },
      };

    default:
      return state;
  }
}
